"use client";

import React, { useCallback, useContext, useMemo } from "react";
import { ReactSVG } from "react-svg";
import { useRouter } from "next/navigation";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { HeroProps } from "./types";
import { Button as ButtonLib } from "@npm_leadtech/qr-ui-components";
import { Typography } from "../Typography";
import { Chip } from "../Chip";
import { AmplitudeContext } from "@/contexts/AmplitudeContext/AmplitudeContext";

import { NextImage } from "../NextImage";
import { paths } from "@/utils/paths";
import "./styles.scss";

const renderChipIcon = (iconPath: string) => (
  <ReactSVG
    src={iconPath}
    className="chip-icon"
    role="img"
    aria-label="chip-icon"
  />
);

export const Hero = ({
  heroText,
  cta,
  chips,
  img,
  className,
  children,
  twoColumns,
  priority,
  size,
  quality,
  unoptimized,
  fetchPriority,
  variant,
}: HeroProps) => {
  const router = useRouter();

  const { setSource } = useContext(AmplitudeContext);
  const { t } = useTranslation("qrcreator-dev-scan-tool");

  const isHome = variant === "home";

  const containerClasses = useMemo(() => {
    return classNames("Hero__container", {
      [`Hero__container--${className}`]: className,
      "Hero__container--home": isHome,
      "Hero__container--twoColumns": twoColumns,
    });
  }, [className, isHome, twoColumns]);

  const handleHeroClick = useCallback(() => {
    setSource("cta_hero_banner");
    router.push(paths.qrEditor());
  }, [router, setSource]);

  const hasValidCta = Boolean(cta?.ctaLabel && cta.ctaLabel.trim() !== "");

  const renderCtaButton = useCallback(
    (extraClass: string) => {
      if (!hasValidCta) return null;
      return (
        <div className={classNames("buttonWrapper", extraClass)}>
          <ButtonLib
            dataQA={cta.data_qa}
            onClick={handleHeroClick}
            label={cta.ctaLabel!}
            variant="secondary"
          />
        </div>
      );
    },
    [cta, hasValidCta, handleHeroClick]
  );

  return (
    <div
      className={containerClasses}
      data-testid="Hero"
    >
      <div className={classNames("Hero__column", { "Hero__column--left": twoColumns })}>
        {chips && chips.length > 0 && (
          <div className="hero_column_chip_content">
            {chips.map((chip, index) => {
              const chipKey = `chip-${chip.label}-${index}`;
              return (
                <Chip
                  key={chipKey}
                  label={t(chip.label)}
                  type={chip.type}
                  color={index === 0 ? "neutral" : "primary"}
                  size="medium"
                  icon={
                    chip.icon
                      ? {
                          position: "left",
                          component: renderChipIcon(chip.icon),
                        }
                      : undefined
                  }
                />
              );
            })}
          </div>
        )}

        {heroText?.map((item, index) => {
          const textKey = `hero-text-${index}`;
          return (
            <Typography
              key={textKey}
              Tag={item.tag}
              size={item.size}
              classname={item.classname}
              spacing={item.spacing}
            >
              {item.list
                ? item.list.map((e, listIndex) => (
                    <li key={`${textKey}-li-${listIndex}`}>
                      <span dangerouslySetInnerHTML={{ __html: e.item }} />
                    </li>
                  ))
                : item.content}
            </Typography>
          );
        })}

        {renderCtaButton("cta-hero-banner-btn")}
      </div>

      {twoColumns && (
        <div className="Hero__column--right">
          {img && (
            <NextImage
              src={img}
              alt="qr grid selection"
              className="hero-img"
              width={633}
              height={379}
              priority={priority}
              size={size}
              quality={quality}
              unoptimized={unoptimized}
              fetchPriority={fetchPriority}
            />
          )}

          {renderCtaButton("cta-mobile")}
        </div>
      )}

      {children}
    </div>
  );
};
